// Practice History — past Daily Focus sets + Weekly Mocks with results.
// Tap a row to review the questions/answers (PracticeReview).

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { examRunwayAPI, HistoryItem } from '../services/examRunwayAPI';
import { useTheme } from '../contexts/ThemeContext';

const PracticeHistory: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const C = {
    border: dark ? '#262c35' : '#e6e8ec',
    muted: dark ? '#aab1ba' : '#64748b',
    text: dark ? '#e6e8eb' : '#0f172a',
    bg: dark ? '#0f1216' : '#f8fafc',
    card: dark ? '#171b21' : '#fff',
    backArrow: dark ? '#aab1ba' : '#475569',
    chevron: dark ? '#7d8590' : '#94a3b8',
  };
  // Same component renders at /practice-history AND under /tg/practice-history
  // (Telegram Mini App). Keep navigation inside the matching prefix.
  const isTg = location.pathname.startsWith('/tg/');
  const base = isTg ? '/tg/practice-history' : '/practice-history';
  const [items, setItems] = useState<HistoryItem[] | null>(null);

  useEffect(() => {
    examRunwayAPI
      .history()
      .then((d) => setItems(d.sessions))
      .catch(() => setItems([]));
  }, []);

  const badge = (t: string) => (t === 'weekly_mock' ? '📊 Mock' : '🎯 Daily');

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          background: C.card,
          borderBottom: `1px solid ${C.border}`,
          position: 'sticky',
          top: 0,
        }}
      >
        <button
          onClick={() => (isTg ? navigate(-1) : navigate('/dashboard'))}
          aria-label="Back"
          style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer', color: C.backArrow }}
        >
          ←
        </button>
        <h1 style={{ margin: 0, fontSize: '1.05rem', color: C.text }}>Practice history</h1>
      </header>

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '12px 16px 80px' }}>
        {items === null && <div style={{ color: C.muted, padding: 16 }}>Loading…</div>}
        {items !== null && items.length === 0 && (
          <div style={{ color: C.muted, padding: 24, textAlign: 'center' }} data-testid="practice-history-empty">
            No sets or mocks yet. Start a Daily Focus to see your history here.
          </div>
        )}
        <div data-testid="practice-history-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(items || []).map((it) => (
            <button
              key={it.session_id}
              data-testid="practice-history-item"
              onClick={() => navigate(`${base}/${it.session_id}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                padding: '12px 14px',
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                cursor: 'pointer',
                textAlign: 'left',
                color: C.text,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, minWidth: 74 }}>{badge(it.type)}</span>
              <span style={{ flex: 1 }}>
                <strong>{it.subject || '—'}</strong>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                  {it.period || ''}
                  {it.mode === 'calibration' ? ' · calibration' : ''} ·{' '}
                  {it.status === 'completed' ? 'completed' : 'in progress'}
                </div>
              </span>
              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                <strong>
                  {it.correct}/{it.total}
                </strong>
                <div style={{ fontSize: 12, color: C.muted }}>{it.score_pct}%</div>
              </span>
              <span style={{ color: C.chevron, fontSize: 18 }}>›</span>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
};

export default PracticeHistory;
